/**
 * Achievements Tab
 *
 * Gamification features - badges, trader profiles, and shareable stats.
 */

import { cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { AnalysisCard } from "./shared/analysis-card";
import { ProgressBar } from "./shared/mini-heatmap";
import { groupAchievementsByCategory } from "../hooks";
import { LockIcon, CheckIcon, ShareIcon, TrophyIcon, TrendingUpIcon, ClockIcon, TargetIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { Achievement } from "@nordex/core";
import { useRef, useState, useEffect, useCallback } from "react";

export function AchievementsTab() {
  const {
    trades, // All-time trades for achievements
    achievements,
    unlockedAchievements,
    totalAchievements,
    profiles,
    primaryProfile,
  } = useTradingAnalysis();

  const isEmpty = !trades.length;

  const groupedAchievements = groupAchievementsByCategory(achievements);

  return (
    <div className="space-y-4">
      {/* Top Section: Trader Profile + Shareable Stats Card - 50/50 */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Trader Profile - 50% - Always show if we have a primary profile */}
        {(primaryProfile || isEmpty) && (
          <div className="lg:w-1/2">
            <AnalysisCard title="Your Trader Profile" className="h-full">
              {isEmpty ? (
                <div className="py-8 text-center">
                  <div className="text-5xl mb-4 opacity-50">🎯</div>
                  <p className="text-sm text-muted-foreground">
                    Complete trades to discover your trader profile
                  </p>
                </div>
              ) : primaryProfile ? (
                <>
                  {/* Primary Profile - Enhanced Display */}
                  <div className="flex items-start gap-4 p-3 bg-primary/5 rounded-xl border border-primary/20">
                    <div className="text-5xl">{primaryProfile.icon}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-foreground">
                        {primaryProfile.label}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {primaryProfile.description}
                      </p>
                      <div className="mt-3 flex items-center gap-3">
                        <div className="flex items-center gap-2 px-2.5 py-1 bg-primary/10 rounded-md">
                          <TargetIcon className="size-3.5 text-primary" />
                          <span className="text-sm font-semibold text-primary">
                            {primaryProfile.confidence}%
                          </span>
                          <span className="text-xs text-muted-foreground">match</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Criteria Badge */}
                  <div className="mt-3 px-3 py-2 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <TrendingUpIcon className="size-3.5" />
                      <span className="font-medium">Key Trait:</span>
                      <span>{primaryProfile.criteria}</span>
                    </div>
                  </div>

                  {/* Secondary Profiles - Always show section */}
                  <div className="mt-4 pt-4 border-t border-border/30">
                    <div className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                      <span>Other Matching Profiles</span>
                      <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[10px]">
                        {profiles.length > 1 ? profiles.length - 1 : 0}
                      </span>
                    </div>
                    {profiles.length > 1 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {profiles.slice(1, 5).map((profile) => (
                          <div
                            key={profile.id}
                            className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg border border-border/30 hover:border-border/50 transition-colors"
                          >
                            <span className="text-xl">{profile.icon}</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium truncate">{profile.label}</div>
                              <div className="text-[10px] text-muted-foreground">
                                {profile.confidence}% match
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-3 bg-muted/10 rounded-lg border border-dashed border-border/50">
                        <ClockIcon className="size-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Keep trading to discover more personality matches
                        </span>
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </AnalysisCard>
          </div>
        )}

        {/* Shareable Stats Card - 50% */}
        <div className={cn("lg:w-1/2", !primaryProfile && !isEmpty && "lg:w-full")}>
          <AnalysisCard
            title="Shareable Stats Card"
            subtitle="Share your performance"
            className="h-full"
            action={
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                onClick={() => {
                  // TODO: Implement share functionality
                  alert("Share functionality coming soon!");
                }}
              >
                <ShareIcon className="size-3.5" />
                <span>Share</span>
              </button>
            }
          >
            <ShareableCardPreview />
          </AnalysisCard>
        </div>
      </div>

      {/* Achievement Progress */}
      <AnalysisCard
        title="Achievements"
        subtitle={`${unlockedAchievements} / ${totalAchievements} unlocked`}
        action={
          <div className="text-xs text-muted-foreground">
            {((unlockedAchievements / totalAchievements) * 100).toFixed(0)}%
            complete
          </div>
        }
      >
        <ProgressBar
          value={(unlockedAchievements / totalAchievements) * 100}
          variant="default"
          className="mb-4"
        />

        {/* Achievements by Category - Horizontal Scroll with Navigation */}
        <div className="space-y-6">
          {(
            [
              ["milestone", "Milestones"],
              ["streak", "Streaks"],
              ["performance", "Performance"],
              ["diversity", "Diversity"],
              ["special", "Special"],
              ["legend", "Legends"],
            ] as const
          ).map(([categoryId, categoryLabel]) => {
            const categoryAchievements = groupedAchievements.get(categoryId);
            if (!categoryAchievements?.length) return null;

            return (
              <AchievementRow 
                key={categoryId}
                categoryLabel={categoryLabel}
                achievements={categoryAchievements}
              />
            );
          })}
        </div>
      </AnalysisCard>
    </div>
  );
}

/**
 * Achievement row with horizontal scroll and navigation arrows
 */
function AchievementRow({ 
  categoryLabel, 
  achievements 
}: { 
  categoryLabel: string; 
  achievements: Achievement[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollPosition = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    checkScrollPosition();
    el.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);
    
    return () => {
      el.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    
    const cardWidth = 180; // Approximate card width + gap
    const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
    
    el.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative group/row">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
        {categoryLabel}
      </h4>
      
      {/* Left Navigation Arrow - Desktop only */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-card/90 border border-border/50 shadow-lg opacity-0 group-hover/row:opacity-100 transition-opacity hover:bg-card hover:border-border"
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="size-4 text-foreground" />
        </button>
      )}
      
      {/* Right Navigation Arrow - Desktop only */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-8 h-8 rounded-full bg-card/90 border border-border/50 shadow-lg opacity-0 group-hover/row:opacity-100 transition-opacity hover:bg-card hover:border-border"
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="size-4 text-foreground" />
        </button>
      )}
      
      {/* Swipeable Container */}
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-3 px-1 snap-x snap-mandatory"
        style={{ 
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {achievements.map((achievement) => (
          <div 
            key={achievement.id}
            className="flex-shrink-0 snap-start"
            style={{ 
              width: 'calc(70% - 8px)',
              minWidth: '160px',
              maxWidth: '200px'
            }}
          >
            <AchievementCard achievement={achievement} />
          </div>
        ))}
        {/* Spacer at end for last card to snap properly */}
        <div className="flex-shrink-0 w-4" aria-hidden="true" />
      </div>
      
      {/* Fade hint on right edge for mobile */}
      <div className="absolute right-0 top-6 bottom-3 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
    </div>
  );
}

/**
 * Format number with appropriate decimals
 */
function formatProgressValue(value: number | undefined): string {
  if (value === undefined) return "";
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}

/**
 * Handle sharing a single achievement
 */
function handleShareAchievement(achievement: Achievement) {
  // For now, just copy achievement info to clipboard
  const shareText = `🏆 I just unlocked "${achievement.label}" on Nordex!\n\n${achievement.icon} ${achievement.description}\n\nStart trading: https://nordex.trade`;
  
  if (navigator.share) {
    navigator.share({
      title: `${achievement.icon} ${achievement.label}`,
      text: shareText,
    }).catch(() => {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareText);
      alert("Achievement copied to clipboard!");
    });
  } else {
    navigator.clipboard.writeText(shareText);
    alert("Achievement copied to clipboard!");
  }
}

/**
 * Individual achievement card with premium styling
 */
function AchievementCard({ achievement }: { achievement: Achievement }) {
  const isUnlocked = achievement.unlocked;

  return (
    <div
      className={cn(
        "relative p-3 rounded-lg border transition-all duration-200 h-full group",
        isUnlocked
          ? "bg-muted/40 border-border/50 shadow-md shadow-black/20 hover:shadow-lg hover:shadow-black/30 hover:-translate-y-0.5 hover:border-border"
          : "bg-muted/20 border-border/30 opacity-60"
      )}
    >
      {/* Icon */}
      <div className="flex items-center justify-between mb-2">
        <span className={cn("text-xl transition-all", !isUnlocked && "grayscale opacity-50")}>
          {achievement.icon}
        </span>
        <div className="flex items-center gap-1">
          {isUnlocked && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShareAchievement(achievement);
              }}
              className="p-1 rounded-md bg-primary/10 text-primary opacity-0 group-hover:opacity-100 hover:bg-primary/20 transition-all"
              title="Share this achievement"
            >
              <ShareIcon className="size-3" />
            </button>
          )}
          {isUnlocked ? (
            <CheckIcon className="size-4 text-bid-foreground" />
          ) : (
            <LockIcon className="size-3 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Info */}
      <div className={cn(
        "text-xs font-medium truncate",
        isUnlocked && "text-foreground"
      )}>
        {achievement.label}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
        {achievement.description}
      </div>

      {/* Progress - Always show, keep default color */}
      <div className="mt-2">
        <ProgressBar 
          value={isUnlocked ? 100 : achievement.progress} 
          variant="default"
        />
        <div className="text-[9px] text-muted-foreground mt-0.5">
          {achievement.targetValue !== undefined && (
            <span>
              {formatProgressValue(isUnlocked ? achievement.targetValue : achievement.currentValue)} / {formatProgressValue(achievement.targetValue)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Shareable stats card preview with hexagon background
 */
function ShareableCardPreview() {
  const { stats, primaryProfile, dateRange, unlockedAchievements } = useTradingAnalysis();

  const isPositive = stats.totalNetPnl >= 0;

  return (
    <div className="w-full">
      {/* Card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card via-card to-muted/30 border border-border/50 p-5">
        {/* Hexagon Background Pattern */}
        <div className="absolute inset-0 opacity-[0.04]">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="hex-pattern-share"
                x="0"
                y="0"
                width="56"
                height="100"
                patternUnits="userSpaceOnUse"
                patternTransform="scale(0.8)"
              >
                <path
                  d="M28,0 L56,16 L56,48 L28,64 L0,48 L0,16 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-foreground"
                />
                <path
                  d="M28,64 L56,80 L56,112 L28,128 L0,112 L0,80 Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                  className="text-foreground"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hex-pattern-share)" />
          </svg>
        </div>

        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm font-medium text-muted-foreground">
              {getDateRangeLabel(dateRange)}
            </div>
            {primaryProfile && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 rounded-full">
                <span className="text-base">{primaryProfile.icon}</span>
                <span className="text-xs font-semibold text-primary">
                  {primaryProfile.label}
                </span>
              </div>
            )}
          </div>

          {/* Main Stats */}
          <div className="text-center mb-5">
            <div
              className={cn(
                "text-4xl font-bold",
                isPositive ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {isPositive ? "+" : ""}${formatCurrency(stats.totalNetPnl)}
            </div>
            <div className="text-sm text-muted-foreground mt-1.5">Net P&L</div>
          </div>

          {/* Stats Grid - 2x2 layout */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-muted/20 rounded-lg text-center">
              <div className="text-lg font-bold text-foreground">
                {stats.winRate.toFixed(0)}%
              </div>
              <div className="text-xs text-muted-foreground">Win Rate</div>
            </div>
            <div className="p-3 bg-muted/20 rounded-lg text-center">
              <div className="text-lg font-bold text-foreground">
                {stats.totalTrades}
              </div>
              <div className="text-xs text-muted-foreground">Trades</div>
            </div>
            <div className="p-3 bg-muted/20 rounded-lg text-center">
              <div className="text-lg font-bold text-foreground">
                🔥 {stats.longestWinStreak}
              </div>
              <div className="text-xs text-muted-foreground">Best Streak</div>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg text-center border border-primary/20">
              <div className="text-lg font-bold text-primary flex items-center justify-center gap-1">
                <TrophyIcon className="size-4" />
                <span>{unlockedAchievements}</span>
              </div>
              <div className="text-xs text-muted-foreground">Achievements</div>
            </div>
          </div>

          {/* Branding */}
          <div className="pt-3 border-t border-border/30 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <span>Trading on</span>
            <span className="font-bold text-foreground">nordex.trade</span>
          </div>
        </div>
      </div>

      <div className="text-center mt-3 text-[10px] text-muted-foreground">
        Click Share to download or copy this card
      </div>
    </div>
  );
}

function getDateRangeLabel(dateRange: import("@/components/ui/date-range-selector").DateRangeValue): string {
  if (typeof dateRange === "object" && dateRange.type === "custom") {
    const { format } = require("date-fns");
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  }
  
  const labels: Record<string, string> = {
    today: "Today",
    yesterday: "Yesterday",
    this_week: "This Week",
    last_week: "Last Week",
    this_month: "This Month",
    last_month: "Last Month",
    last_7_days: "Last 7 Days",
    last_30_days: "Last 30 Days",
    last_90_days: "Last 90 Days",
    ytd: "Year to Date",
    all: "All Time",
  };
  return labels[dateRange as string] || (dateRange as string);
}

function formatCurrency(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}
